import React, { useState } from "react";
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import toast from 'react-hot-toast'; 
import { useNavigate, useLocation } from "react-router-dom";
import {
  AppBar,
  Box,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Toolbar,
  Typography,
  Avatar,
  useMediaQuery,
  useTheme,
  Tooltip,
} from "@mui/material";
import {
  Menu as MenuIcon,
  Inventory2Outlined as InventoryIcon,
  ShoppingCartOutlined as OrdersIcon,
  CategoryOutlined as CategoryIcon,
  AccountTreeOutlined as SubCategoryIcon,
  LogoutOutlined as LogoutIcon,
} from "@mui/icons-material";

const drawerWidth = 260;

const colors = {
  sidebarBg: "#0f172a",
  sidebarHover: "#1e293b",
  activeTint: "#38bdf8",
  textPrimary: "#f8fafc",
  textSecondary: "#94a3b8",
  appBarBg: "rgba(255, 255, 255, 0.8)",
};

export default function Header() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const handleDrawerToggle = () => setMobileOpen(!mobileOpen);

  const handleLogout = () => {
    localStorage.removeItem("login");
    // ✅ Emoji ke saath success message
    toast.success("Logged out safely! See you soon 👋", {
      duration: 4000,
      icon: '🚀', // Aap yahan apna favourite emoji badal sakte ho
    });
    navigate("/"); 
  };

  const menuItems = [
    { icon: <InventoryIcon />, label: "Products", path: "/admin/Products" },
    { icon: <OrdersIcon />, label: "Orders", path: "/admin/Orders" },
    { icon: <CategoryIcon />, label: "Categories", path: "/admin/Category" },
    { icon: <SubCategoryIcon />, label: "Sub Categories", path: "/admin/SubCategory" },
    { icon: <LocalOfferIcon />, label: "Manage Offers", path: "/admin/offermanager" }
  ];

  const drawer = (
    <Box sx={{ height: "100%", display: "flex", flexDirection: "column", bgcolor: colors.sidebarBg }}>
      <Box sx={{ p: 3, display: "flex", alignItems: "center", gap: 1.5 }}>
        <Box sx={{ width: 35, height: 35, bgcolor: colors.activeTint, borderRadius: "8px", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: "bold", color: "#fff" }}>
          C
        </Box>
        <Typography variant="h6" sx={{ color: colors.textPrimary, fontWeight: 700, fontSize: "1.1rem" }}>
          <i> RoopShingar admin</i>
        </Typography>
      </Box>

      <Box sx={{ px: 2, mt: 2, flexGrow: 1 }}>
        <Typography variant="caption" sx={{ color: colors.textSecondary, fontWeight: 600, pl: 1, mb: 1, display: "block", textTransform: "uppercase", letterSpacing: 1 }}>
          Main Menu
        </Typography>
        <List>
          {menuItems.map((item) => {
            const isActive = location.pathname === item.path;
            return (
              <ListItem key={item.path} disablePadding sx={{ mb: 0.5 }}>
                <ListItemButton
                  onClick={() => {
                    navigate(item.path);
                    if (isMobile) setMobileOpen(false);
                  }}
                  sx={{
                    borderRadius: "10px",
                    bgcolor: isActive ? "rgba(56, 189, 248, 0.1)" : "transparent",
                    color: isActive ? colors.activeTint : colors.textSecondary,
                    "&:hover": { bgcolor: colors.sidebarHover, color: "#fff" },
                    py: 1.2,
                  }}
                >
                  <ListItemIcon sx={{ color: "inherit", minWidth: 40 }}>
                    {item.icon}
                  </ListItemIcon>
                  <ListItemText
                    primary={item.label}
                    primaryTypographyProps={{ fontSize: "0.9rem", fontWeight: isActive ? 600 : 500 }}
                  />
                </ListItemButton>
              </ListItem>
            );
          })}
        </List>
      </Box>

      <Box sx={{ p: 2, bgcolor: "rgba(0,0,0,0.2)" }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, p: 1 }}>
          <Avatar sx={{ width: 36, height: 36, bgcolor: colors.activeTint }}>A</Avatar>
          <Box sx={{ overflow: "hidden" }}>
            <Typography variant="body2" sx={{ color: "#fff", fontWeight: 600 }}>
              Admin User
            </Typography>
            <Typography variant="caption" sx={{ color: colors.textSecondary, display: "block" }}>
              Super Admin
            </Typography>
          </Box>
          <Tooltip title="Logout">
            <IconButton onClick={handleLogout} sx={{ color: colors.textSecondary, ml: "auto", "&:hover": { color: "#ef4444" } }}>
              <LogoutIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>
    </Box>
  );

  return (
    <>
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          width: { md: `calc(100% - ${drawerWidth}px)` },
          ml: { md: `${drawerWidth}px` },
          bgcolor: colors.appBarBg,
          backdropFilter: "blur(8px)",
          borderBottom: "1px solid #e2e8f0",
          color: "#1e293b",
          // ✅ Z-INDEX FIX: MUI AppBar by default 1100 hota hai. 
          // Ise 1000 kar dete hain taaki Toaster (default 9999) hamesha upar rahe.
          zIndex: 1000, 
        }}
      >
        <Toolbar sx={{ justifyContent: "space-between" }}>
          <IconButton
            color="inherit"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { md: "none" } }}
          >
            <MenuIcon />
          </IconButton>
          
          <Typography variant="subtitle1" sx={{ fontWeight: 600, color: "#64748b" }}>
            Dashboard / <span style={{ color: "#1e293b" }}>Overview</span>
          </Typography>

          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
             <Typography variant="body2" sx={{ color: "#94a3b8", display: { xs: "none", sm: "block" } }}>
                {new Date().toLocaleDateString("en-US", { weekday: "long", month: "short", day: "numeric" })}
             </Typography>
          </Box>
        </Toolbar>
      </AppBar>

      <Box component="nav" sx={{ width: { md: drawerWidth }, flexShrink: { md: 0 } }}>
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: "block", md: "none" },
            "& .MuiDrawer-paper": { boxSizing: "border-box", width: drawerWidth, border: "none" },
            zIndex: 1100, // Drawer ko AppBar se upar rakhne ke liye
          }}
        >
          {drawer}
        </Drawer>
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: "none", md: "block" },
            "& .MuiDrawer-paper": { boxSizing: "border-box", width: drawerWidth, borderRight: "none" },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>
    </>
  );
}